import apiClient from "./ApiClient.js";

// =====================================================
// TOKEN MANAGEMENT
// =====================================================

const tokenManager = {
  set(token) {
    if (!token) return;

    localStorage.setItem("token", token);

    // Axios ke default headers mein token set
    apiClient.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${token}`;
  },

  get() {
    return localStorage.getItem("token");
  },

  remove() {
    localStorage.removeItem("token");

    delete apiClient.defaults.headers.common[
      "Authorization"
    ];
  },

  exists() {
    return Boolean(localStorage.getItem("token"));
  },
};

// =====================================================
// LOGIN SERVICE
// =====================================================

const loginService = {
  /**
   * Admin Login
   *
   * POST /admin/login
   *
   * If 2FA required:
   * {
   *   payload: {
   *     twoFactorRequired: true
   *   }
   * }
   *
   * If normal login:
   * {
   *   payload: {
   *     token: "..."
   *   }
   * }
   */
  async execute(email, password) {
    const response = await apiClient.post(
      "admin/login",
      {
        email: email.trim(),
        password,
      }
    );

    const data = response.data;

    // ===============================================
    // 2FA REQUIRED
    // ===============================================

    if (
      data?.payload?.twoFactorRequired === true
    ) {
      // IMPORTANT:
      // Yahan normal login token save nahi karna.

      return data;
    }

    // ===============================================
    // NORMAL LOGIN
    // ===============================================

    const token = data?.payload?.token;

    if (token) {
      tokenManager.set(token);
    }

    return data;
  },

  /**
   * Verify Login 2FA OTP
   *
   * POST /admin/verify-2fa-otp
   */
  async verify2fa(email, otp) {
    const response = await apiClient.post(
      "admin/verify-2fa-otp",
      {
        email: email.trim(),
        otp: String(otp).trim(),
      }
    );

    const data = response.data;

    // ===============================================
    // TOKEN AFTER OTP SUCCESS
    // ===============================================

    const token = data?.payload?.token;

    if (token) {
      tokenManager.set(token);
    }

    return data;
  },
};

// =====================================================
// PASSWORD RESET SERVICE
// =====================================================

const passwordResetService = {
  /**
   * Forgot Password
   *
   * POST /admin/forget-password
   */
  async forgot(email) {
    const response = await apiClient.post(
      "admin/forget-password",
      {
        email: email.trim(),
      }
    );

    return response.data;
  },

  /**
   * Verify Forgot Password OTP
   *
   * POST /admin/verify-otp
   */
  async verify(email, otp) {
    const response = await apiClient.post(
      "admin/verify-otp",
      {
        email: email.trim(),
        otp: String(otp).trim(),
      }
    );

    return response.data;
  },

  /**
   * Set New Password
   *
   * POST /admin/set-new-password
   */
  async reset(
    email,
    newPassword,
    confirmPassword
  ) {
    const response = await apiClient.post(
      "admin/set-new-password",
      {
        email: email.trim(),
        newPassword,
        confirmPassword,
      }
    );

    return response.data;
  },
};

// =====================================================
// AUTH STATUS
// =====================================================

const authStatus = {
  isAuthenticated() {
    return tokenManager.exists();
  },

  logout() {
    tokenManager.remove();

    localStorage.removeItem("userData");
    localStorage.removeItem("userName");
    localStorage.removeItem("isLoggedIn");

    window.location.href =
      "/admin/login";
  },
};

// =====================================================
// EXPORT COMPONENTS
// =====================================================

export {
  loginService,
  passwordResetService,
  tokenManager,
  authStatus,
};

// =====================================================
// LEGACY EXPORTS
// =====================================================

export const loginAdmin =
  loginService.execute;

export const verify2faOtp =
  loginService.verify2fa;

export const forgotPassword =
  passwordResetService.forgot;

export const verifyOtp =
  passwordResetService.verify;

export const resetPassword =
  passwordResetService.reset;

export const logoutAdmin =
  authStatus.logout;

export const getAuthToken =
  tokenManager.get;

export const isAuthenticated =
  authStatus.isAuthenticated;
